import React, { useEffect, useMemo, useState } from 'react';
import { Table as AntTable, TableProps, Card, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import './style.scss';

interface MobileTableProps extends TableProps<any> {
  onRowClick?: (record: any) => void;
}

const MobileTable: React.FC<MobileTableProps> = ({ onRowClick, ...tableProps }) => {
  const [isMobile, setIsMobile] = useState(false);
  const { t } = useTranslation();

  const paginationProp = tableProps.pagination;
  const paginationObj =
    paginationProp && typeof paginationProp === 'object' ? paginationProp : null;

  const [internalPage, setInternalPage] = useState(1);
  const [internalPageSize, setInternalPageSize] = useState(() =>
    paginationObj && typeof paginationObj.pageSize === 'number' ? paginationObj.pageSize : 20
  );

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const dataSource: any[] = (tableProps.dataSource as any[]) || [];

  // Client-side pagination (Ant Design behavior): when total is omitted, paginate over local rows.
  const isClientSide = !!paginationObj && paginationObj.total == null;

  useEffect(() => {
    if (!isClientSide) return;
    setInternalPage(1);
  }, [dataSource.length, isClientSide]);

  const current =
    paginationObj && typeof paginationObj.current === 'number'
      ? paginationObj.current
      : internalPage;
  const pageSize =
    paginationObj && typeof paginationObj.pageSize === 'number'
      ? paginationObj.pageSize
      : internalPageSize;
  const total = paginationObj?.total != null ? Number(paginationObj.total) : dataSource.length;

  const pageRows = useMemo(() => {
    if (!paginationObj || !isClientSide) return dataSource;
    const start = (current - 1) * pageSize;
    return dataSource.slice(start, start + pageSize);
  }, [paginationObj, isClientSide, dataSource, current, pageSize]);

  if (!isMobile) {
    return <AntTable {...tableProps} />;
  }

  const columns: any[] = (tableProps.columns as any[]) || [];
  const loading = tableProps.loading;
  const rowKey = tableProps.rowKey || 'id';

  const getRowKey = (record: any, index: number) => {
    if (typeof rowKey === 'function') return rowKey(record, index);
    return record[rowKey as string] ?? index;
  };

  const getLabel = (column: any): React.ReactNode => {
    const title = column.title;
    if (!title) return column['data-label'] || column.dataIndex || '';
    if (typeof title === 'string') return title;
    if (typeof title === 'function') {
      try {
        return title({});
      } catch {
        return column['data-label'] || column.dataIndex || '';
      }
    }
    if (React.isValidElement(title)) return title;
    if (title?.props?.children) return title.props.children;
    return column['data-label'] || column.dataIndex || '';
  };

  const getValue = (column: any, record: any, index: number): React.ReactNode => {
    const dataIndex = column.dataIndex;
    let raw: any;
    if (Array.isArray(dataIndex)) {
      raw = dataIndex.reduce((obj: any, key: string) => obj?.[key], record);
    } else if (dataIndex) {
      raw = record[dataIndex];
    }

    if (column.render) {
      return column.render(raw, record, index);
    }
    if (raw !== undefined && raw !== null) {
      return typeof raw === 'object' ? JSON.stringify(raw) : String(raw);
    }
    return '-';
  };

  const actionsCol = columns.find((c) => c.key === 'actions');
  const dataCols = columns.filter((c) => c.key !== 'actions');

  const totalPages = paginationObj ? Math.max(1, Math.ceil(total / pageSize) || 1) : 0;

  const handlePageChange = (page: number, size: number = pageSize) => {
    if (paginationObj?.current == null) {
      setInternalPage(page);
    }
    if (paginationObj?.pageSize == null && size !== pageSize) {
      setInternalPageSize(size);
    }

    paginationObj?.onChange?.(page, size);

    if (tableProps.onChange) {
      tableProps.onChange(
        {
          ...(paginationObj || {}),
          current: page,
          pageSize: size,
          total,
        },
        {},
        {},
        { currentDataSource: dataSource, action: 'paginate' }
      );
    }
  };

  return (
    <div className="mobile-table-wrapper">
      {loading ? (
        <div className="mt-state">
          <span>{t('table.loading', 'Loading...')}</span>
        </div>
      ) : dataSource.length === 0 ? (
        <div className="mt-state">
          <span>{t('table.noData', 'No data')}</span>
        </div>
      ) : (
        pageRows.map((record, rowIdx) => {
          const absoluteIndex = isClientSide ? (current - 1) * pageSize + rowIdx : rowIdx;
          return (
            <Card
              key={getRowKey(record, absoluteIndex)}
              className={`mt-card${onRowClick ? ' mt-card-tap' : ''}`}
              onClick={() => onRowClick?.(record)}
            >
              {dataCols.map((col: any, colIdx: number) => {
                const label = getLabel(col);
                const value = getValue(col, record, absoluteIndex);
                return (
                  <div key={col.key || col.dataIndex || colIdx} className="mt-field">
                    <span className="mt-label">{label}:</span>
                    <span className="mt-value">{value}</span>
                  </div>
                );
              })}
              {actionsCol?.render && (
                <div className="mt-actions" onClick={(e) => e.stopPropagation()}>
                  {actionsCol.render(undefined, record, absoluteIndex)}
                </div>
              )}
            </Card>
          );
        })
      )}

      {paginationObj && total > 0 && (
        <div className="mt-pagination">
          <Button
            size="small"
            disabled={current <= 1}
            onClick={() => handlePageChange(current - 1, pageSize)}
          >
            {t('table.previous', 'Previous')}
          </Button>
          <span className="mt-page-info">
            {current} / {totalPages}
          </span>
          <Button
            size="small"
            disabled={current >= totalPages}
            onClick={() => handlePageChange(current + 1, pageSize)}
          >
            {t('table.next', 'Next')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default MobileTable;
